import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Printer, Boxes, ScanLine, Webhook, Shirt, Calculator, PackageSearch, LogOut, Lock, Users, Zap, FileText, History, ClipboardList, Tags } from "lucide-react";

const links = [
  { to: "/produzione", label: "Produzione DTF", icon: Printer, testid: "nav-dtf-link" },
  { to: "/raccolta-ordini", label: "Raccolta Ordini", icon: ClipboardList, testid: "nav-raccolta-link" },
  { to: "/catalogo-articoli", label: "Catalogo Articoli", icon: Tags, testid: "nav-catalogo-link", adminOnly: true, hideForNonAdmin: true },
  { to: "/drop", label: "Gestione Drop", icon: Shirt, testid: "nav-drop-link" },
  { to: "/magazzino", label: "Gestione Magazzino", icon: Boxes, testid: "nav-inventory-link" },
  { to: "/preventivatore", label: "Preventivatore", icon: Calculator, testid: "nav-calc-link" },
  { to: "/preventivi", label: "Preventivi", icon: FileText, testid: "nav-preventivi-link" },
  { to: "/vendita-rapida", label: "Vendita Rapida", icon: Zap, testid: "nav-quote-link" },
  { to: "/storico-vendite", label: "Storico Vendite", icon: History, testid: "nav-storico-link" },
  { to: "/ricerca-ordini", label: "Ricerca Ordini", icon: PackageSearch, testid: "nav-orders-link" },
  { to: "/scanner", label: "Scanner Barcode", icon: ScanLine, testid: "nav-scanner-link", adminOnly: true },
  { to: "/simulatore", label: "Simulatore Shopify", icon: Webhook, testid: "nav-webhook-link", adminOnly: true },
  { to: "/utenti", label: "Gestione Utenti", icon: Users, testid: "nav-users-link", adminOnly: true, hideForNonAdmin: true },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen grid-bg text-slate-200">
      {/* Top header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-700/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Gigli Edition" className="h-10 w-auto" />
            <div className="leading-tight border-l border-slate-700/60 pl-2.5 hidden sm:block">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Magazzino & Produzione DTF</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 hidden sm:inline" data-testid="user-email">{user?.email}</span>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-red-400 transition-colors px-2 py-1.5 rounded-md hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
        {/* Nav pills */}
        <nav className="max-w-7xl mx-auto px-3 sm:px-6 pb-2 flex gap-1.5 overflow-x-auto">
          {links.map((l) => {
            if (l.hideForNonAdmin && !isAdmin) return null;
            const locked = l.adminOnly && !isAdmin;
            if (locked) {
              return (
                <span
                  key={l.to}
                  data-testid={l.testid}
                  title="Riservato all'amministratore"
                  aria-disabled="true"
                  className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap text-slate-500 opacity-40 blur-[1px] cursor-not-allowed select-none pointer-events-none"
                >
                  <Lock className="w-3.5 h-3.5" />
                  {l.label}
                </span>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/40"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/40 border border-transparent"
                  }`
                }
              >
                <l.icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            );
          })}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 fade-in">{children}</main>
    </div>
  );
};
